import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { ScrapCertificateService } from './scrap-certificate.service';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private cartItemCount = new BehaviorSubject<number>(0);
  cartItemCount$ = this.cartItemCount.asObservable();

    // Update cart count from API (for logged-in users)
    updateCartCountFromApi(): void {
      const payload = {
        offset: 0,
        limit: 100,
        action_type: 'GET_LIST',
        get_selected_parts: ''
      };
      this.apiService.getCartData(payload).subscribe({
        next: (res: any) => {
          if (res.success && res.data && res.data.cart_items) {
            const count = res.data.cart_items.length;
            this.cartItemCount.next(count);
            if (isPlatformBrowser(this.platformId)) {
              localStorage.setItem('cartItemCount', count.toString());
            }
          } else {
            this.cartItemCount.next(0);
            if (isPlatformBrowser(this.platformId)) {
              localStorage.setItem('cartItemCount', '0');
            }
          }
        },
        error: (err: any) => {
          console.error('Failed to fetch cart count:', err);
        }
      });
    }

  constructor(
    private apiService: ApiService,
    private router: Router,
    private scrapCertificateService: ScrapCertificateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Initialize cart count from localStorage if available and in browser environment
    if (isPlatformBrowser(this.platformId)) {
      this.initializeCartCount();
    }
  }

  private initializeCartCount(): void {
    const count = localStorage.getItem('cartItemCount');
    if (count) {
      this.cartItemCount.next(parseInt(count));
    }
  }

  // Method to add item to cart for logged-in users
  addToCart(partNumber: string, deliveryType: string = 'Store Pickup', quantity?: number, unit?: string): Observable<any> {
    const part_type = localStorage.getItem('addtocartpart_type');
    
    // Calculate quantity based on part type and unit
    let calculatedQuantity: number;
    if (part_type === 'Used Part') {
      calculatedQuantity = quantity && quantity > 0 ? quantity : 1;
    } else {
      // For scrap parts, handle unit conversion
      if (quantity !== undefined && quantity !== null && quantity > 0 && unit) {
        // Convert tons to kg, keep kg and liter as is
        if (unit === 'ton' || unit === 'Ton') {
          calculatedQuantity = quantity * 1000; // Convert tons to kg
        } else if (unit === 'kg' || unit === 'Kg') {
          calculatedQuantity = quantity; // Keep kg as is
        } else if (unit === 'liter' || unit === 'Litr') {
          calculatedQuantity = quantity; // Keep liter as is
        } else {
          calculatedQuantity = quantity; // Default: use quantity as is
        }
      } else {
        calculatedQuantity = 2000; // Default fallback
      }
    }
    
    const payload = {
      offset: 0,
      limit: 10,
      action_type: 'ADD_DATA',
      validate_request: true,
      source: 'Online',
      part_number_list: [partNumber],
      cart_items: [
        {
          part_number: partNumber,
          delivery_type: deliveryType,
          part_type: part_type,
          total_quantity: calculatedQuantity,
        },
      ],
    };

    return new Observable((observer) => {
      this.apiService.addToCart(payload).subscribe({
        next: (res) => {
          // Increment cart count
          const currentCount = this.cartItemCount.value + 1;
          this.cartItemCount.next(currentCount);

          // Only update localStorage in browser environment
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('cartItemCount', currentCount.toString());
          }

          observer.next(res);
          observer.complete();
        },
        error: (err) => {
          console.error('Error adding to cart:', err);
          observer.error(err);
        },
      });
    });
  }

  // Method to add item to local storage for non-logged-in users
  addToLocalStorage(
    productData: any,
    deliveryType: string = 'Store Pickup',
    quantity?: number,
    unit?: string
  ): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      // Get existing local storage cart items
      const existingItems = this.getLocalStorageCartItems();

      // Check if item already exists
      const existingItemIndex = existingItems.findIndex(
        (item) => item.part_number === productData.part_number
      );

      if (existingItemIndex === -1) {
        // Calculate quantity for scrap parts
        let calculatedQuantity: number;
        if (productData?.part_type === 'Scrap Part' && quantity && unit) {
          calculatedQuantity = unit === 'Ton' ? quantity * 1000 : quantity; // Convert tons to kg
        } else {
          calculatedQuantity = productData?.part_type === 'Used Parts' ? 1 : 2000;
        }
        
        // Add new item with complete product details
        const cartItem = {
          ...productData, // Store complete product details
          delivery_type: deliveryType,
          quantity: calculatedQuantity,
          selected_quantity: quantity || 1,
          selected_unit: unit || 'Kg',
          added_at: new Date().toISOString(),
          is_local_storage_item: true, // Flag to identify local storage items
        };

        existingItems.push(cartItem);

        // Save back to localStorage
        localStorage.setItem(
          'localStorageCartItems',
          JSON.stringify(existingItems)
        );

        // Update cart count
        const currentCount = this.cartItemCount.value + 1;
        this.cartItemCount.next(currentCount);
        localStorage.setItem('cartItemCount', currentCount.toString());
      } else {
      }
    } catch (error) {
      console.error('Error adding item to local storage cart:', error);
    }
  }

  // Method to get local storage cart items
  getLocalStorageCartItems(): any[] {
    if (!isPlatformBrowser(this.platformId)) {
      return [];
    }

    try {
      const items = localStorage.getItem('localStorageCartItems');
      return items ? JSON.parse(items) : [];
    } catch (error) {
      console.error('Error getting local storage cart items:', error);
      return [];
    }
  }

  // Method to clear local storage cart items
  clearLocalStorageCart(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      localStorage.removeItem('localStorageCartItems');
    } catch (error) {
      console.error('Error clearing local storage cart:', error);
    }
  }

  // Remove a single item from local storage cart (guest users)
  removeFromLocalStorage(partNumber: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const existingItems = this.getLocalStorageCartItems();
      const newItems = existingItems.filter(
        (item) => item.part_number !== partNumber
      );

      if (newItems.length !== existingItems.length) {
        localStorage.setItem(
          'localStorageCartItems',
          JSON.stringify(newItems)
        );

        const newCount = Math.max(this.cartItemCount.value - 1, 0);
        this.cartItemCount.next(newCount);
        localStorage.setItem('cartItemCount', newCount.toString());
      }
    } catch (error) {
      console.error('Error removing item from local storage cart:', error);
    }
  }

  // Remove multiple items from local storage cart (guest users)
  removeMultipleFromLocalStorage(partNumbers: string[]): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const set = new Set(partNumbers);
      const existingItems = this.getLocalStorageCartItems();
      const newItems = existingItems.filter(
        (item) => !set.has(item.part_number)
      );
      const removedCount = existingItems.length - newItems.length;

      if (removedCount > 0) {
        localStorage.setItem(
          'localStorageCartItems',
          JSON.stringify(newItems)
        );

        const newCount = Math.max(this.cartItemCount.value - removedCount, 0);
        this.cartItemCount.next(newCount);
        localStorage.setItem('cartItemCount', newCount.toString());
      }
    } catch (error) {
      console.error('Error removing items from local storage cart:', error);
    }
  }

  // Method to sync local storage cart items to server after login
  syncLocalStorageToServer(): Observable<any> {
    return new Observable((observer) => {
      if (!isPlatformBrowser(this.platformId)) {
        observer.next({ success: true, message: 'Not in browser environment' });
        observer.complete();
        return;
      }

      const localItems = this.getLocalStorageCartItems();

      if (localItems.length === 0) {
        observer.next({ success: true, message: 'No local items to sync' });
        observer.complete();
        return;
      }

      // Prepare cart items for API call (extract only required fields)
      const cartItemsForAPI = localItems.map((item) => ({
        part_number: item.part_number,
        delivery_type: item.delivery_type,
      }));

      const payload = {
        offset: 0,
        limit: 10,
        action_type: 'ADD_DATA',
        part_number_list: localItems.map((item) => item.part_number),
        cart_items: cartItemsForAPI,
      };

      this.apiService.addToCart(payload).subscribe({
        next: (res) => {
          // Clear local storage cart after successful sync
          this.clearLocalStorageCart();

          // Update cart count to reflect the synced items
          const currentCount = this.cartItemCount.value + localItems.length;
          this.cartItemCount.next(currentCount);
          localStorage.setItem('cartItemCount', currentCount.toString());

          observer.next(res);
          observer.complete();
        },
        error: (err) => {
          console.error('Error syncing local storage items to server:', err);
          observer.error(err);
        },
      });
    });
  }

  // Method to check if user has items in local storage
  hasLocalStorageItems(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const items = this.getLocalStorageCartItems();
    return items.length > 0;
  }

  // Method to get total cart count (local + server)
  getTotalCartCount(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return this.cartItemCount.value;
    }

    const localItems = this.getLocalStorageCartItems();
    return this.cartItemCount.value + localItems.length;
  }

  navigateToCart(): void {
    this.router.navigate(['/cart']);
  }

  // Method to update cart count (can be used when fetching initial cart data)
  updateCartCount(count: number): void {
    this.cartItemCount.next(count);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('cartItemCount', count.toString());
    }
  }

  /**
   * Handle add to cart with scrap part detection
   * This method should be used by all components instead of direct addToCart calls
   * @param product - The product to add to cart
   * @param deliveryType - Delivery type (default: 'Store Pickup')
   * @param quantity - Quantity (optional)
   * @param unit - Unit (optional)
   * @param addToCartCallback - Callback for non-scrap parts (optional)
   * @param loginCallback - Callback for login required (optional)
   */
  handleAddToCart(
    product: any, 
    deliveryType: string = 'Store Pickup', 
    quantity?: number, 
    unit?: string,
    addToCartCallback?: () => void,
    loginCallback?: () => void
  ): void {
    // Use the enhanced scrap certificate service
    this.scrapCertificateService.handleAddToCart(
      product,
      addToCartCallback || (() => {
        // Fallback: add directly to cart
        const partNumber = product.part_number || product.part_detail?.part_number;
        if (partNumber) {
          this.addToCart(partNumber, deliveryType, quantity, unit).subscribe({
            next: () => console.log('Item added to cart'),
            error: (err) => console.error('Error adding to cart:', err)
          });
        }
      }),
      loginCallback
    );
  }
}
